import { Mutation } from "@apollo/client/react/components";
import { RawDraftContentState } from "draft-js";
import pick from "lodash/fp/pick";
import React, { ReactElement } from "react";
import CreateCommentMutation from "~/lib/mutations/CreateComment";
import {
  ICreateCommentMutation,
  ICreateCommentMutationVariables,
} from "~/lib/mutations/__generated__/CreateComment.generated";
import CommentsQuery from "~/lib/queries/CommentsQuery";
import optimisticId from "~/lib/utils/optimisticId";
import { ICommentTargetType, IUser } from "~/types/types";
import Auth from "~/components/auth/Auth";
import convertUserTypenameToAuthor from "~/components/comment/convertUserTypenameToAuthor";

interface ICreateCommentProps {
  targetId: string;
  targetType: ICommentTargetType;
  children: (createComment: (body: RawDraftContentState) => void) => ReactElement | null;
}

export default function CreateComment({ targetId, targetType, children, ...props }: ICreateCommentProps): ReactElement {
  return (
    <Mutation<ICreateCommentMutation, ICreateCommentMutationVariables>
      update={(proxy, { data }) => {
        const queryResult = proxy.readQuery({
          query: CommentsQuery,
          variables: {
            targetId,
            targetType,
            cursor: null,
          },
        });

        const { comments } = queryResult as any;

        if (data) {
          proxy.writeQuery({
            query: CommentsQuery,
            variables: {
              targetId,
              targetType,
              cursor: null,
            },
            data: {
              comments: {
                ...comments,
                items: [data.createComment].concat(comments.items),
              },
            },
          });
        }
      }}
      mutation={CreateCommentMutation}
      {...props}
    >
      {(createComment) => {
        return (
          <Auth>
            {({ user }) => {
              return children((body: RawDraftContentState) => {
                const createdAt = new Date().toISOString();

                return createComment({
                  variables: {
                    targetId,
                    targetType,
                    body: body as any,
                  },
                  optimisticResponse: {
                    __typename: "Mutation",
                    createComment: {
                      __typename: "Comment" as any,
                      _id: optimisticId(),
                      body: body as any,
                      upvotesCount: 0,
                      viewerDidUpvote: false,
                      createdAt: createdAt as any,
                      lastUpdatedAt: null,
                      isReply: false,
                      author: {
                        ...(pickCommentAuthorFields(user) as any),
                        __typename: convertUserTypenameToAuthor(user as IUser) as any,
                      },
                      replies: [],
                    },
                  },
                });
              });
            }}
          </Auth>
        );
      }}
    </Mutation>
  );
}

const pickCommentAuthorFields = pick(["_id", "slug", "firstName", "fullName", "jobTitle", "avatar", "isInnovator"]);
